import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  Student,
  CreateStudentDto,
  UpdateStudentDto,
} from '../../../../core/interfaces/student';
import { LearningPath } from '../../../../core/interfaces/learning-path';
import { StudentService } from '../../../../core/services/student.service';
import { CustomSelectComponent } from '../../../../components/ui/custom-select/custom-select.component';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Subject } from '../../../../core/interfaces/subject';
import { LearningService } from '../../../../core/services/learning-path.service';
import { PackAdminService } from '../../../../core/services/pack-admin.service';
import { LocalizedLabelService } from '../../../../core/services/utils/localized-label.service';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { Pack } from '../../../../core/interfaces/pack';
import { selectableSubjects } from '../../../utils/selectable-subjects';
import { generateTemporaryPassword } from '../../../utils/temporary-password';
import { ChangeDetectorRef, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
@Component({
  selector: 'app-add-student',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CommonModule, TranslateModule, CustomSelectComponent],
  templateUrl: './add-student.component.html',
  styleUrl: './add-student.component.css',
})
export class AddStudentComponent implements OnInit, OnChanges {
  private fb = inject(FormBuilder);
  private translate = inject(TranslateService);
  private studentService = inject(StudentService);
  private learningService = inject(LearningService);
  private packAdmin = inject(PackAdminService);
  private labels = inject(LocalizedLabelService);
  private swal = inject(SwalService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  @Input() student: Student | null = null;
  @Input() isEditMode = false;
  @Input() learningPathId: number | null = null;
  @Input() learningPaths: LearningPath[] = [];

  /**
   * Un enregistrement est en cours côté parent.
   *
   * <p>Le bouton ne se désactivait que sur la validité du formulaire : pendant
   * l'appel, il restait cliquable. Un second clic — ou un double-clic — créait
   * l'élève une fois, puis se heurtait à l'unicité de l'adresse et rendait un
   * 409. L'administrateur voyait alors une erreur de création alors que l'élève
   * venait bien d'être créé, et repartait le créer une troisième fois.</p>
   *
   * <p>Le drapeau vit chez le parent, qui seul sait quand l'appel se termine —
   * un drapeau interne resterait levé après un échec et interdirait de
   * réessayer.</p>
   */
  @Input() saving = false;

  @Output() saved = new EventEmitter<CreateStudentDto | UpdateStudentDto>();
  @Output() cancelled = new EventEmitter<void>();

  studentForm!: FormGroup;
  isCheckingCode = false;
  codeAvailable: boolean | null = null;
  subjects: Subject[] = [];
  selectedSubjectIds: number[] = [];
  isLoadingSubjects = false;
  subjectOptions: any[] = [];
  selectKey = 0;

  /**
   * Les packs proposables à la création (SKILL2ERA-137) : le pack décide de
   * ce que l'élève voit dans son espace. Le serveur l'enregistre comme une
   * inscription validée, la même source que pour un élève inscrit en ligne.
   */
  packOptions: { id: number; title: string }[] = [];
  private packs: Pack[] = [];
  private packsRequested = false;

  /** La liste des packs n'a pas pu être lue : l'écran dit pourquoi le champ est vide. */
  packsUnavailable = false;

  /**
   * Nouveau mot de passe proposé pour un élève existant. Comme à la création,
   * il est temporaire : l'élève en choisit un autre à sa connexion suivante.
   */
  newPassword = '';
  isResettingPassword = false;

  /**
   * Le formulaire est en cours de remplissage depuis l'eleve edite.
   *
   * <p>Renseigner le parcours declenche l'ecoute qui vide les matieres — c'est
   * ce qu'on veut quand l'utilisateur change de parcours, jamais quand on
   * repose la fiche existante : les matieres affectees seraient effacees a la
   * seconde meme ou on vient de les precocher.</p>
   */
  private populating = false;
ngOnInit(): void {
  this.initializeForm();
  this.populateFormIfEdit();
  this.setupStudentCodeValidation();
  // Les libellés suivent la langue de l'écran, pas celle du chargement.
  this.translate.onLangChange
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => this.buildPackOptions());

  const initialPathId = this.studentForm.get('learningPathId')?.value;
  if (initialPathId) {
    this.loadSubjects(initialPathId);
  }

  // 2. Ecoute des changements du niveau d'étude
  this.studentForm.get('learningPathId')?.valueChanges.subscribe((pathId) => {

    // Repose de la fiche : on ne touche a rien, les matieres viennent d'etre
    // precochees et le chargement est declenche par populateFormIfEdit.
    if (this.populating) return;

    // ACTION 1 : Vider la valeur dans le formulaire
    this.studentForm.get('subjectIds')?.setValue([]);
    
    // ACTION 2 : "Tuer" le composant visuellement (Reset UI)
    this.subjectOptions = [];
    this.selectKey = -1; // On force le *ngIf à false
    
    this.cdr.detectChanges(); 

    if (pathId) {
      // ACTION 3 : Charger les nouvelles matières
      this.loadSubjects(pathId);
    }
  });
}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['student'] && !changes['student'].firstChange) {
      this.populateFormIfEdit();
    }
    if (changes['isEditMode'] && !changes['isEditMode'].firstChange) {
      this.populateFormIfEdit();
    }
  }

  private initializeForm(): void {
    this.studentForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      studentCode: ['', [Validators.required, Validators.minLength(3)]],
      learningPathId: [null, [Validators.required]],
      subjectIds: [[]],
      packId: [null],
      password: [''],
      enrollmentDate: [''],
      isActive: [true],
    });
  }

  /** Une seule lecture, et seulement à la création : la modification n'a pas de champ pack. */
  private loadPacks(): void {
    if (this.packsRequested) return;
    this.packsRequested = true;
    this.packsUnavailable = false;

    this.packAdmin.getAll().subscribe({
      next: (res) => {
        this.packs = (res.data ?? []).filter((p) => p.isActive);
        this.buildPackOptions();
      },
      error: () => {
        this.packsRequested = false;
        this.packsUnavailable = true;
      },
    });
  }

  private buildPackOptions(): void {
    this.packOptions = this.packs.map((p) => ({ id: p.id, title: this.labels.pick(p) }));
  }

  /**
   * Pack et mot de passe ne se saisissent qu'à la création : le pack devient
   * l'inscription, le mot de passe le premier identifiant. La même modale sert
   * à modifier, où ces champs n'existent pas.
   */
  private applyCreateOnlyFields(): void {
    const pack = this.studentForm.get('packId');
    const password = this.studentForm.get('password');
    pack?.setValidators(this.isEditMode ? [] : [Validators.required]);
    password?.setValidators(this.isEditMode ? [] : [Validators.required, Validators.minLength(8)]);
    pack?.updateValueAndValidity({ emitEvent: false });
    password?.updateValueAndValidity({ emitEvent: false });
    if (!this.isEditMode) this.loadPacks();
  }

  /** Propose un nouveau mot de passe ; l'administrateur peut le modifier avant d'enregistrer. */
  generatePassword(): void {
    this.studentForm.get('password')?.setValue(generateTemporaryPassword());
  }

  generateNewPassword(): void {
    this.newPassword = generateTemporaryPassword();
  }

  /** Pose un mot de passe temporaire sur le compte de l'élève modifié. */
  resetPassword(): void {
    if (!this.student || this.newPassword.length < 8 || this.isResettingPassword) return;

    this.isResettingPassword = true;
    this.studentService.resetPassword(this.student.id, this.newPassword).subscribe({
      next: () => {
        this.isResettingPassword = false;
        this.swal.success(this.translate.instant('ADMIN.PASSWORD_RESET_DONE'));
      },
      error: (err) => {
        this.isResettingPassword = false;
        this.swal.error(err?.error?.message || this.translate.instant('ADMIN.PASSWORD_RESET_FAILED'));
      },
    });
  }

  private setupStudentCodeValidation(): void {
    this.studentForm
      .get('studentCode')
      ?.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((code) => {
          if (!code || code.length < 3) {
            this.codeAvailable = null;
            return of(null);
          }
          this.isCheckingCode = true;
          const excludeId =
            this.isEditMode && this.student ? this.student.id : undefined;
          return this.studentService.checkStudentCodeAvailability(
            code,
            excludeId,
          );
        }),
      )
      .subscribe({
        next: (result) => {
          this.isCheckingCode = false;
          if (result) {
            // Signalé, jamais bloquant : le serveur n'impose pas l'unicité du
            // code élève — c'est un choix assumé, fixé par un test. Poser une
            // erreur de formulaire ici interdirait une création que l'API
            // accepte, et le front déciderait d'une règle métier à sa place.
            this.codeAvailable = result.isAvailable;
          }
        },
        error: () => {
          this.isCheckingCode = false;
          this.codeAvailable = null;
        },
      });
  }

  /**
   * Identifiants des matieres deja affectees a l'eleve edite.
   *
   * <p>Le serveur les expose sous `studentSubjects` ; on n'en garde que les
   * identifiants, seuls attendus par le selecteur.</p>
   */
  private assignedSubjectIds(): number[] {
    return (this.student?.studentSubjects ?? [])
      .map((ss) => ss.subjectId)
      .filter((id): id is number => Number.isFinite(id));
  }

  private populateFormIfEdit(): void {
    this.applyCreateOnlyFields();
    this.newPassword = '';
    this.populating = true;
    try {
      this.populateFormValues();
    } finally {
      this.populating = false;
    }

    // Les matieres du parcours repose : sans ce chargement, le selecteur
    // resterait vide et les identifiants precoches n'auraient rien a afficher.
    const pathId = this.studentForm.get('learningPathId')?.value;
    if (this.isEditMode && pathId) this.loadSubjects(Number(pathId));
  }

  private populateFormValues(): void {
    if (this.isEditMode && this.student) {
      this.studentForm.patchValue({
        firstName: this.student.firstName,
        lastName: this.student.lastName,
        email: this.student.email,
        studentCode: this.student.studentCode,
        learningPathId: this.student.learningPathId,
        enrollmentDate: this.student.enrollmentDate
          ? this.formatDateForInput(this.student.enrollmentDate)
          : '',
        isActive: this.student.isActive ?? true,
        // Les matieres affectees, que le formulaire ouvrait vides : ne pas les
        // reporter revenait a les effacer au premier enregistrement, puisque
        // c'est la valeur du champ qui est envoyee.
        subjectIds: this.assignedSubjectIds(),
      });
    } else if (!this.isEditMode) {
      // Set default values for new student
      this.studentForm.reset({
        firstName: '',
        lastName: '',
        email: '',
        studentCode: '',
        learningPathId: this.learningPathId,
        packId: null,
        // Un mot de passe recommandé, d'emblée : l'administrateur peut le garder,
        // le régénérer ou le remplacer.
        password: generateTemporaryPassword(),
        enrollmentDate: new Date().toISOString().split('T')[0],
        isActive: true,
      });
    }
  }

  onSubmit(): void {
    // Deuxième rempart derrière le bouton désactivé : entre deux clics très
    // rapprochés, la détection de changements n'a pas forcément propagé
    // `saving` jusqu'à l'attribut du bouton.
    if (this.saving) return;

    if (this.studentForm.invalid) {
      this.studentForm.markAllAsTouched();
      return;
    }

    // Pas de journal du formulaire : il porte le mot de passe initial.
    const formValue = this.studentForm.value;

    if (this.isEditMode && this.student) {
      // Update mode
      const updateData: UpdateStudentDto = {
        id: this.student.id,
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        email: formValue.email,
        studentCode: formValue.studentCode,
        learningPathId: Number(formValue.learningPathId),
        enrollmentDate: formValue.enrollmentDate || undefined,
        isActive: Boolean(formValue.isActive),
        subjectIds: formValue.subjectIds || [],
        
      };
      this.saved.emit(updateData);
    } else {
      // Create mode
      const createData: CreateStudentDto = {
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        email: formValue.email,
        studentCode: formValue.studentCode,
        learningPathId: Number(formValue.learningPathId),
        enrollmentDate: formValue.enrollmentDate || undefined,
        isActive: Boolean(formValue.isActive),
        subjectIds: formValue.subjectIds || [],
        packId: Number(formValue.packId),
        password: formValue.password,
      };
      this.saved.emit(createData);
    }
  }

loadSubjects(pathId: number): void {
  this.isLoadingSubjects = true;
  this.cdr.detectChanges();

  // Toutes les matières du parcours, pour n'en garder que les actives et
  // celles déjà affectées à l'élève : une inscription à une matière désactivée
  // depuis doit rester cochée, sinon le premier enregistrement l'effacerait.
  this.learningService
    .getSubjectsByLearningPath(pathId, false, false)
    .subscribe({
      next: (response) => {
        this.subjects = selectableSubjects(response.data, this.assignedSubjectIds());

        // Préparer les nouvelles options
        const newOptions = this.subjects.map(s => ({
          id: s.id,
          title: s.name
        }));

        // Utiliser setTimeout pour laisser à Angular le temps de digérer le reset
        setTimeout(() => {
          this.subjectOptions = [...newOptions];
          this.isLoadingSubjects = false;
          
          // On réincrémente pour recréer une instance propre du select
          this.selectKey = Math.random(); // Utiliser un random est plus sûr que ++ ici
          
          this.cdr.detectChanges();
        }, 0);
      },
      error: (error) => {
        console.error('Error loading subjects:', error);
        this.isLoadingSubjects = false;
        this.selectKey = 0; // On le remet quand même pour ne pas bloquer l'UI
        this.cdr.detectChanges();
      }
    });
}

  onCancel(): void {
    this.studentForm.reset({
      firstName: '',
      lastName: '',
      email: '',
      studentCode: '',
      learningPathId: this.learningPathId,
      packId: null,
      password: generateTemporaryPassword(),
      enrollmentDate: new Date().toISOString().split('T')[0],
      isActive: true,
    });
    this.codeAvailable = null;
    this.cancelled.emit();
  }

  // Getters for form controls
  get firstName() {
    return this.studentForm.get('firstName');
  }

  get lastName() {
    return this.studentForm.get('lastName');
  }

  get email() {
    return this.studentForm.get('email');
  }

  get studentCode() {
    return this.studentForm.get('studentCode');
  }

  get learningPath() {
    return this.studentForm.get('learningPathId');
  }

  get enrollmentDate() {
    return this.studentForm.get('enrollmentDate');
  }

  get isActive() {
    return this.studentForm.get('isActive');
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.studentForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getErrorMessage(fieldName: string): string {
    const field = this.studentForm.get(fieldName);
    if (!field || !field.errors) return '';

    if (field.errors['required']) {
      return this.translate.instant('VALIDATION.REQUIRED');
    }
    if (field.errors['minlength']) {
      return this.translate.instant('VALIDATION.MIN_LENGTH', {
        min: field.errors['minlength'].requiredLength,
      });
    }
    if (field.errors['email']) {
      return this.translate.instant('VALIDATION.EMAIL_INVALID');
    }
    if (field.errors['codeTaken']) {
      return this.translate.instant('VALIDATION.STUDENT_CODE_TAKEN');
    }

    return '';
  }

  private formatDateForInput(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }
}
